import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { filterIncludedItem } from 'utilities/hooks/responseHandlers';
import type { MarketplaceAccount } from 'types/MarketplaceAccount';
import type { SparkFavourite } from 'types/Spark/SparkFavourite';
import type { SingleSparkResponse, SparkResponse } from 'types/Spark/Spark';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import type { File } from 'types/File';
import {
  JoinedMemory,
  MemoryCategory,
  MemoryFavourite,
  MemoryRating,
  MemoryResponse,
  MemorySearch,
  MemorySearchResults,
  SingleMemoryResponse,
} from 'types/Memory';
import { SparkImage } from 'types/Spark/SparkImages';
import { MemoryImages } from 'types/Memory/MemoryImages';
import { SparkCategory } from 'types/entityTypes/SparkCategories';
import { SparkRating } from 'types/Spark/SparkRating';

const createSingleJoinedSpark = (
  response: SingleSparkResponse | undefined
): JoinedSparks | undefined => {
  const spark = response?.data;
  const included = response?.included;
  if (!spark) return;

  const sparkFavourite = filterIncludedItem<SparkFavourite>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_FAVOURITES
  );

  const sparkImage = filterIncludedItem<SparkImage>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_IMAGES
  );
  const sparkCategories = filterIncludedItem<SparkCategory>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_CATEGORIES
  );

  const sparkRatings = filterIncludedItem<SparkRating>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_RATINGS
  );

  return {
    ...spark,
    sparkCategories: sparkCategories,
    sparkFavourites: sparkFavourite?.filter(
      (favourite) => favourite.attributes.sparkID === spark.id
    ),
    sparkImages: sparkImage?.filter(
      (image) => image.attributes.sparkID === spark.id
    ),
    sparkRatings,
  };
};

const createJoinedSparks = (
  sparkResponse: SparkResponse | undefined
): JoinedSparks[] | undefined => {
  const sparks = sparkResponse?.data;
  const included = sparkResponse?.included;

  const sparkFavourite = filterIncludedItem<SparkFavourite>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_FAVOURITES
  );

  const joinedSparks: JoinedSparks[] | undefined = sparks?.map((spark) => {
    return {
      ...spark,
      totalCount: sparkResponse?.meta?.total,
      sparkFavourites: sparkFavourite?.filter(
        (favourite) => favourite.attributes.sparkID === spark.id
      ),
    };
  });
  return joinedSparks;
};

const createSingleJoinedMemory = (
  memoryResponse: SingleMemoryResponse | undefined
): JoinedMemory | undefined => {
  const memory = memoryResponse?.data;
  const included = memoryResponse?.included;
  if (!memory) {
    return;
  }
  const marketplaceAccount = filterIncludedItem<MarketplaceAccount>(
    included,
    SPARK_RESOURCE_TYPES.MARKETPLACE_ACCOUNTS
  )?.[0];

  const memorySearches = filterIncludedItem<MemorySearch>(
    included,
    SPARK_RESOURCE_TYPES.MEMORY_SEARCHES
  );

  const memorySearchResults = filterIncludedItem<MemorySearchResults>(
    included,
    SPARK_RESOURCE_TYPES.MEMORY_SEARCH_RESULTS
  );
  const memoryCategories = filterIncludedItem<MemoryCategory>(
    included,
    SPARK_RESOURCE_TYPES.MEMORY_CATEGORIES
  );
  const memoryImages = filterIncludedItem<MemoryImages>(
    included,
    SPARK_RESOURCE_TYPES.MEMORY_IMAGES
  );

  const files = filterIncludedItem<File>(included, SPARK_RESOURCE_TYPES.FILES);
  const memoryRatings = filterIncludedItem<MemoryRating>(
    included,
    SPARK_RESOURCE_TYPES.MEMORY_RATINGS
  );
  const memoryFavourite = filterIncludedItem<MemoryFavourite>(
    included,
    SPARK_RESOURCE_TYPES.MEMORY_FAVOURITES
  );

  return {
    ...memory,
    memoryCategories,
    marketplaceAccount,
    memoryRatings,
    memoryImages,
    files,
    memorySearches,
    memorySearchResults,
    memoryFavourite,
  };
};
const createJoinedMemories = (response: MemoryResponse): JoinedMemory[] => {
  const memories = response?.data;
  const included = response?.included;

  const memoryFavourite = filterIncludedItem<MemoryFavourite>(
    included,
    SPARK_RESOURCE_TYPES.MEMORY_FAVOURITES
  );

  const joinedMemory: JoinedMemory[] | undefined = memories?.map((memory) => {
    return {
      ...memory,
      totalCount: response?.meta?.total,
      memoryFavourites: memoryFavourite?.filter(
        (favourite) => favourite.attributes.memoryID === memory.id
      ),
    };
  });
  return joinedMemory;
};

export {
  createSingleJoinedSpark,
  createJoinedSparks,
  createSingleJoinedMemory,
  createJoinedMemories,
};
